import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Key, Copy, Check, ArrowRight, Code, Zap, Terminal, ChevronRight } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";
import QuantumBackground from "@/components/QuantumBackground";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CODE_SNIPPETS = {
  python: `from openai import OpenAI

client = OpenAI(
    api_key="YOUR_KEY",
    base_url="https://api.qnneal.dev/v1"
)

response = client.chat.completions.create(
    model="gpt-4o",
    messages=[{"role": "user", "content": "Hello!"}]
)
print(response.choices[0].message.content)`,

  javascript: `const response = await fetch("https://api.qnneal.dev/v1/chat/completions", {
  method: "POST",
  headers: {
    "Authorization": "Bearer YOUR_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    model: "gpt-4o",
    messages: [{ role: "user", content: "Hello!" }]
  })
});
const data = await response.json();
console.log(data.choices[0].message.content);`,

  curl: `curl https://api.qnneal.dev/v1/chat/completions \\
  -H "Authorization: Bearer YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "gpt-4o",
    "messages": [{"role": "user", "content": "Hello!"}]
  }'`,
};

export default function Onboarding() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [step, setStep] = useState(1);
  const [keyName, setKeyName] = useState("My First Key");
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeSnippet, setActiveSnippet] = useState("python");

  const handleCreateKey = async () => {
    setCreating(true);
    try {
      const res = await axios.post(`${API}/keys/create`, { name: keyName, permissions: "full" }, { withCredentials: true });
      setCreatedKey(res.data);
      setStep(2);
      toast.success("API key created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create key");
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 3000);
  };

  const handleFinish = async () => {
    try {
      await axios.patch(`${API}/profile`, { }, { withCredentials: true });
    } catch {}
    navigate("/dashboard");
  };

  const bgColor = isDark ? "bg-slate-50 dark:bg-[#0A0A1A]" : "bg-[#F8F9FC]";
  const textMuted = isDark ? "text-white/50" : "text-gray-500";

  return (
    <div className={`min-h-screen ${bgColor} relative flex items-center justify-center p-6`}>
      <div className="fixed inset-0 z-0"><QuantumBackground /></div>
      <div className="fixed inset-0 z-[1] pointer-events-none" style={{ background: isDark ? "radial-gradient(ellipse 60% 60% at 50% 40%, transparent 0%, rgba(5,5,15,0.8) 100%)" : "" }} />

      <motion.div className="relative z-10 w-full max-w-xl" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3].map(s => (
            <div key={s} className={`h-1.5 rounded-full transition-all ${s <= step ? "bg-[var(--qn-primary)] w-12" : "bg-white/10 w-8"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {/* Step 1: Create API Key */}
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <Card className="border-white/10" style={{ background: isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)" }}>
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--qn-primary)]/10 flex items-center justify-center">
                    <Key className="w-8 h-8 text-[var(--qn-primary)]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-2">Get Your API Key</h2>
                    <p className={`text-sm ${textMuted}`}>
                      This is like a password that lets your apps talk to QNNEAL. You'll paste it into your code.
                    </p>
                  </div>
                  <div className="text-left">
                    <label className={`text-sm ${textMuted}`}>Give your key a name</label>
                    <Input value={keyName} onChange={e => setKeyName(e.target.value)} placeholder="My First Key" className="mt-1 bg-white/5 border-white/10 text-center text-lg" data-testid="onboarding-key-name" autoFocus />
                  </div>
                  <Button onClick={handleCreateKey} disabled={creating || !keyName.trim()} className="w-full py-6 text-lg bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90" data-testid="onboarding-create-key">
                    <Key className="w-5 h-5 mr-2" />
                    {creating ? "Creating..." : "Create My API Key"}
                  </Button>
                  <button onClick={() => navigate("/dashboard")} className={`text-sm ${textMuted} hover:text-[var(--qn-primary)]`} data-testid="onboarding-skip">
                    Skip for now <ArrowRight className="w-3 h-3 inline ml-1" />
                  </button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Copy Key */}
          {step === 2 && createdKey && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <Card className="border-white/10" style={{ background: isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)" }}>
                <CardContent className="p-8 text-center space-y-6">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-500/10 flex items-center justify-center">
                    <Check className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h2 className="text-2xl font-bold">Your API Key is Ready!</h2>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-sm p-3 rounded-lg">
                    Copy this key now. You won't be able to see it again.
                  </div>

                  <div className="bg-black/30 rounded-xl p-4 border border-white/10">
                    <code className="text-sm font-mono text-emerald-300 break-all select-all" data-testid="created-key-display">
                      {createdKey.full_key}
                    </code>
                  </div>

                  <Button
                    onClick={() => handleCopy(createdKey.full_key)}
                    className={`w-full py-6 text-lg transition-all ${copied ? "bg-emerald-600 hover:bg-emerald-600" : "bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90"}`}
                    data-testid="copy-api-key-btn"
                  >
                    {copied ? (
                      <><Check className="w-5 h-5 mr-2" /> COPIED! You're all set.</>
                    ) : (
                      <><Copy className="w-5 h-5 mr-2" /> COPY API KEY</>
                    )}
                  </Button>

                  <Button variant="outline" onClick={() => setStep(3)} className="w-full py-4 border-white/10" data-testid="onboarding-next-step">
                    Continue <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Quick Start */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <Card className="border-white/10" style={{ background: isDark ? "rgba(10,10,26,0.85)" : "rgba(255,255,255,0.95)", backdropFilter: "blur(16px)" }}>
                <CardContent className="p-8 space-y-6">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-500/10 flex items-center justify-center mb-3">
                      <Zap className="w-8 h-8 text-blue-400" />
                    </div>
                    <h2 className="text-2xl font-bold">You're Ready!</h2>
                    <p className={`text-sm ${textMuted} mt-1`}>Choose your setup to get started</p>
                  </div>

                  {createdKey && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className={textMuted}>Your key:</span>
                      <code className="font-mono text-emerald-300 text-xs">{createdKey.key_preview}</code>
                      <button onClick={() => handleCopy(createdKey.full_key)} className="text-[var(--qn-primary)] hover:underline text-xs">[Copy]</button>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {Object.keys(CODE_SNIPPETS).map(lang => (
                      <Button
                        key={lang}
                        variant={activeSnippet === lang ? "default" : "outline"}
                        size="sm"
                        onClick={() => setActiveSnippet(lang)}
                        className={activeSnippet === lang ? "bg-[var(--qn-primary)]" : "border-white/10"}
                        data-testid={`snippet-${lang}`}
                      >
                        {lang === "python" && <Code className="w-3 h-3 mr-1" />}
                        {lang === "javascript" && <Code className="w-3 h-3 mr-1" />}
                        {lang === "curl" && <Terminal className="w-3 h-3 mr-1" />}
                        {lang.charAt(0).toUpperCase() + lang.slice(1)}
                      </Button>
                    ))}
                  </div>

                  <div className="relative">
                    <pre className="bg-black/40 rounded-lg p-4 text-xs font-mono text-emerald-300 overflow-x-auto border border-white/10 whitespace-pre-wrap">
                      {CODE_SNIPPETS[activeSnippet].replace(/YOUR_KEY/g, createdKey?.key_preview || "qng_sk_...")}
                    </pre>
                    <button
                      onClick={() => handleCopy(CODE_SNIPPETS[activeSnippet].replace(/YOUR_KEY/g, createdKey?.full_key || "qng_sk_..."))}
                      className="absolute top-2 right-2 text-xs text-white/40 hover:text-white bg-white/5 px-2 py-1 rounded"
                    >
                      Copy
                    </button>
                  </div>

                  <Button onClick={handleFinish} className="w-full py-6 text-lg bg-[var(--qn-primary)] hover:bg-[var(--qn-primary)]/90" data-testid="onboarding-finish">
                    Go to My Dashboard <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>

        <p className={`text-center text-xs mt-4 ${textMuted}`}>Step {step} of 3</p>
      </motion.div>
    </div>
  );
}
